// eslint-disable-next-line @typescript-eslint/no-explicit-any
let mswWorker: any = null
let mswStarted = false

// HMR-safe cleanup: preserve MSW state across hot module replacements
if (import.meta.hot) {
  import.meta.hot.accept()
  import.meta.hot.dispose(() => {
    // Don't stop MSW on HMR - it will be reused
    // Just mark that we need to check state on next use
  })
}

async function startMocking(): Promise<boolean> {
  // Check if already started (HMR-safe)
  if (mswStarted && mswWorker) {
    console.log('🎭 Demo mode: MSW already running')
    return true
  }

  try {
    const { worker } = await import('~~/mocks/browser')
    mswWorker = worker

    // Check if worker is already started (e.g., from HMR)
    // MSW doesn't expose a direct "isRunning" check, so we track it ourselves
    await worker.start({
      onUnhandledRequest: 'bypass',
      quiet: true
    })

    mswStarted = true
    console.log('🎭 Demo mode: MSW started')
    return true
  } catch (error) {
    console.warn('🎭 Demo mode: Failed to start MSW', error)
    mswStarted = false
    mswWorker = null
    return false
  }
}

async function stopMocking(): Promise<void> {
  if (mswWorker) {
    try {
      await mswWorker.stop()
      console.log('🎭 Demo mode: MSW stopped')
    } catch (error) {
      console.warn('🎭 Demo mode: Error stopping MSW', error)
    }
    mswWorker = null
    mswStarted = false
  }
}

export const useDemo = () => {
  const isDemoMode = useState<boolean>('demo:active', () => {
    return sessionStorage.getItem('demo-mode') === 'true'
  })

  const demoDelay = useState<'fast' | 'normal' | 'slow' | 'custom'>('demo:delay', () => {
    return (sessionStorage.getItem('demo-delay') as 'fast' | 'normal' | 'slow' | 'custom') || 'normal'
  })

  /**
   * Enables demo mode: clears existing auth state, starts MSW, and sets demo flags.
   */
  const enableDemo = async (delay: 'fast' | 'normal' | 'slow' | 'custom' = 'normal') => {
    // Clear any existing auth state first to prevent mixing
    const { clearAuthState } = useAuth()
    clearAuthState()

    // Set demo mode flags
    isDemoMode.value = true
    demoDelay.value = delay
    sessionStorage.setItem('demo-mode', 'true')
    sessionStorage.setItem('demo-delay', delay)

    // Start MSW
    await startMocking()
  }

  /**
   * Disables demo mode: stops MSW, clears all demo and auth state.
   */
  const disableDemo = async () => {
    // Clear auth state first
    const { clearAuthState } = useAuth()
    clearAuthState()

    // Clear demo useState values
    isDemoMode.value = false
    demoDelay.value = 'normal'

    // Clear sessionStorage
    sessionStorage.removeItem('demo-mode')
    sessionStorage.removeItem('demo-delay')

    // Clear localStorage demo keys (MFA state)
    localStorage.removeItem('demo:mfaEnabled')

    // Stop MSW
    await stopMocking()
  }

  const setDemoDelay = (delay: 'fast' | 'normal' | 'slow' | 'custom') => {
    demoDelay.value = delay
    sessionStorage.setItem('demo-delay', delay)
  }

  /**
   * Exits demo mode and redirects to login page.
   */
  const exitDemoAndRedirect = async () => {
    await disableDemo()
    await navigateTo('/login')
  }

  /**
   * Ensures MSW is running if demo mode is active (for HMR recovery).
   */
  const ensureMSWRunning = async () => {
    if (isDemoMode.value && !mswStarted) {
      await startMocking()
    }
  }

  return {
    isDemoMode: readonly(isDemoMode),
    demoDelay: readonly(demoDelay),
    enableDemo,
    disableDemo,
    setDemoDelay,
    exitDemoAndRedirect,
    ensureMSWRunning
  }
}
